/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
//  https://login.microsoftonline.com/681e41cd-3aea-474d-9e84-92c95cba16c1

export const msalConfig = {
  auth: {
    validateAuthority: true,
    clientId: process.env.REACT_APP_MSAL_CLIENTID
      ? process.env.REACT_APP_MSAL_CLIENTID
      : "", // This is the ONLY mandatory field that you need to supply.
    authority:
      process.env.REACT_APP_MSAL_AUTHORITY ||
      "https://login.microsoftonline.com/681e41cd-3aea-474d-9e84-92c95cba16c1", // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: window.location.origin, // Keep redirect on app root to avoid intermittent blank-page auth dead-ends.
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // Reduces repeated redirect hops when auth response is processed.
  },
  cache: {
    cacheLocation: "localStorage", // Persist auth cache across reloads to reduce repeated interactive logins.
    storeAuthStateInCookie: true,
  },
  // system: {
  //   loggerOptions: {
  //     loggerCallback: (level: any, message: any, containsPii: any) => {
  //       if (containsPii) {
  //         return;
  //       }
  //       switch (level) {
  //         case LogLevel.Error:
  //           console.error(message);
  //           return;
  //         case LogLevel.Info:
  //           console.info(message);
  //           return;
  //         case LogLevel.Verbose:
  //           console.debug(message);
  //           return;
  //         case LogLevel.Warning:
  //           console.warn(message);
  //           return;
  //       }
  //     },
  //   },
  // },
};

// TODO: .env
export const LOGIN_SCOPES = ["openid", "profile", "user.read"];
export const API_SCOPES = [
  `${process.env.REACT_APP_API_SCOPE}/access_user_data`,
];

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiEndpoints: {
    categoriesEndpoint: process.env.REACT_APP_API_BASEURL + "/api/categories",
    tenantEndpoint: process.env.REACT_APP_API_BASEURL + "/api/tenants",
    tenantActionsEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/tenantactions",
    actionsEndpoint: process.env.REACT_APP_API_BASEURL + "/api/actions",
    commentsEndpoint: process.env.REACT_APP_API_BASEURL + "/api/comments",
    actionCommentsEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/actioncomments",
    alertsEndpoint: process.env.REACT_APP_API_BASEURL + "/api/alerts",
    templateActionsEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/TemplateActions",
    customerInfoEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/customerInfos",
    statusEndpoint: process.env.REACT_APP_API_BASEURL + "/api/statuses",
    wikiEndpoint: process.env.REACT_APP_API_BASEURL + "/api/wikiPages",
    subPageEndpoint: process.env.REACT_APP_API_BASEURL + "/api/subpages",
    imageEndpoint: process.env.REACT_APP_API_BASEURL + "/api/files",
    assignmentEndpoint: process.env.REACT_APP_API_BASEURL + "/api/assignments",
    customerOverviewSnapshotsEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/customerOverviewSnapshots",
    customerRunbookExecutionsEndpoint:
      process.env.REACT_APP_API_BASEURL + "/api/customerRunbookExecutions",
    // scopes: [process.env.REACT_APP_API_SCOPE],
  },
  apiGraph: {
    endpoint: "https://graph.microsoft.com/v1.0/me/memberOf",
    scopes: ["User.Read", "GroupMember.Read.All"],
  },
};

// NOT IN USE
export const securityGroups = {
  GroupMember: process.env.REACT_APP_API_GROUPMEMBER,
  GroupAdmin: process.env.REACT_APP_API_GROUPADMIN,
};

const parseJwt = (token: string) => {
  try {
    const parsedJwt = JSON.parse(atob(token.split(".")[1]));
    return parsedJwt;
  } catch (e) {
    return null;
  }
};

export const hasAzAdminRole = (rawIdToken: string): boolean => {
  const parsedToken = parseJwt(rawIdToken);
  return isAzAdmin(parsedToken?.roles);
};

export const hasRoleFromClaims = (roles: unknown): boolean => {
  return isAzAdmin(roles);
};

export const isAzAdmin = (roles: any): boolean => {
  return roles?.includes("CCuser");
};

export const readRole = "CCuser";
